import React, { useState } from 'react';
import { Box, Button, Tooltip, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GenerateBedCatCal } from '../../Api/PropertyMst/Api';
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import { getFormMode } from '../../lib/helpers';
import TableComponent from '../../atoms/TableComponent/RecordSummaryCalendar';
import 'react-toastify/dist/ReactToastify.css';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectProperties,
    selectGlobalProperty,
    selectLocalProperty,
    setLocalProperty,
    fetchProperties,
    selectPropertyStatus
} from '../../redux/propertySlice';
import axiosInstance from '../../lib/axios';

const Form_Mode = getFormMode();

const FIXED_COLUMNS = [
    { id: 'RoomTypeName', label: 'RoomType', minWidth: 120 },
    { id: 'RoomBedTypeName', label: 'BedType', minWidth: 70 },
    { id: 'Total', label: 'Total', minWidth: 70 },
    { id: 'Jul-25', label: 'Jul-25', minWidth: 70 },
    { id: 'Aug-25', label: 'Aug-25', minWidth: 70 },
    { id: 'Sep-25', label: 'Sep-25', minWidth: 70 },
    { id: 'Oct-25', label: 'Oct-25', minWidth: 70 },
    { id: 'Nov-25', label: 'Nov-25', minWidth: 70 },
    { id: 'Dec-25', label: 'Dec-25', minWidth: 70 },
    { id: 'Jan-26', label: 'Jan-26', minWidth: 70 },
    { id: 'Feb-26', label: 'Feb-26', minWidth: 70 },
    { id: 'Mar-26', label: 'Mar-26', minWidth: 70 },
    { id: 'Apr-26', label: 'Apr-26', minWidth: 70 },
    { id: 'May-26', label: 'May-26', minWidth: 70 },
    { id: 'June-26', label: 'June-26', minWidth: 70 },
];

const getDynamicColumns = (startDate, endDate) => {
    const months = [];
    let current = dayjs(startDate).startOf('month');

    while (current.isBefore(endDate) || current.isSame(endDate, 'month')) {
        const label = current.format('MMM-YY');
        months.push({ id: label, label, minWidth: 70 });
        current = current.add(1, 'month');
    }

    return [
        { id: 'RoomTypeName', label: 'RoomType', minWidth: 120 },
        { id: 'RoomBedTypeName', label: 'BedType', minWidth: 90 },
        { id: 'Total', label: 'Total Count', minWidth: 70 },
        ...months
    ];
};

const transformApiData = (apiData, startDate, endDate) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    const monthKeys = {};
    let current = dayjs(startDate).startOf('month');
    while (current.isBefore(endDate) || current.isSame(endDate, 'month')) {
        const label = current.format('MMM-YY');
        monthKeys[current.format('MMMM')] = label; // e.g., 'July': 'Jul-25'
        current = current.add(1, 'month');
    }

    const aggregated = {};

    apiData.forEach(item => {
        const key = `${item.RoomTypeName}-${item.RoomBedTypeName}`;
        if (!aggregated[key]) {
            aggregated[key] = {
                RoomTypeName: item.RoomTypeName,
                RoomBedTypeName: item.RoomBedTypeName,
                months: {}
            };
        }

        const monthKey = monthKeys[item.MonthNm];
        if (!monthKey) return;

        aggregated[key].months[monthKey] = {
            total: (item.BedToSale || 0) + (item.ExtraBed || 0),
            extra: item.ExtraBed || 0,
            bedtosale: item.BedToSale || 0,
            booked: item.BookingBeds || 0,
            occupied: item.OccBeds || 0,
            balance: item.BalBed || 0
        };
    });

    const rows = [];

    Object.values(aggregated).forEach(({ RoomTypeName, RoomBedTypeName, months }) => {
        const createRow = (metricLabel, metricKey) => {
            const row = {
                RoomTypeName,
                RoomBedTypeName,
                Total: metricLabel
            };

            Object.keys(monthKeys).forEach(monthName => {
                const monthCol = monthKeys[monthName];
                row[monthCol] = months[monthCol]?.[metricKey] || 0;
            });
            return row;
        };

        rows.push(createRow('Total Bed', 'total'));
        rows.push(createRow('Extra Bed', 'extra'));
        rows.push(createRow('BedToSale', 'bedtosale'));
        rows.push(createRow('Booked ', 'booked'));
        rows.push(createRow('Occupied ', 'occupied'));
        rows.push(createRow('Balance', 'balance'));
    });

    return rows;
};

export default function BedCategorySummary() {
    const userId = sessionStorage.getItem('UserId');
    const location = useLocation();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [rows, setRows] = useState([]);
    const [dateFrom, setDateFrom] = useState(dayjs());
    const [dateTo, setDateTo] = useState(dayjs().add(11, 'months'));
    const [tableColumns, setTableColumns] = useState(FIXED_COLUMNS);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        totalRecords: 0,
        currentPage: 1,
        totalPages: 0,
        pageSize: 15
    });
    const [form, setForm] = useState({ PropId: "" });
    const dispatch = useDispatch();
    const properties = useSelector(selectProperties);
    const globalProperty = useSelector(selectGlobalProperty);
    const localProperty = useSelector(state => selectLocalProperty(state, 'calendar'));
    const propertyStatus = useSelector(selectPropertyStatus);
    const selectedProperty = localProperty || globalProperty;

    const [mode, setMode] = useState(() =>
        location?.state?.RoomId ? Form_Mode.read : Form_Mode.add
    );
    const permissions = usePermissions("Record Calendar");

    const fetchData = async () => {
        if (!selectedProperty) {
            return toast.error("Please select a property");
        }

        const monthDiff = dateTo.diff(dateFrom, 'month');

        if (monthDiff < 0) {
            return toast.warning("To date selection can't earlier than from date.");
        }

        if (monthDiff > 12) {
            return toast.warning("Date Range Can't exceed 12 months.")
        }

        try {
            setIsLoading(true);
            setRows([]);

            const payload = {
                PropId: selectedProperty,
                BookDtFrom: dateFrom.format("YYYY-MM-DD"),
                CheckoutDt: dateTo.format("YYYY-MM-DD"),
                Flag: "Summary"
            };

            const response = await GenerateBedCatCal(payload);

            if (response?.data?.Status === 0) {
                const apiData = response.data.Data?.BedCatCalList || [];

                const columns = getDynamicColumns(dateFrom, dateTo);
                setTableColumns(columns);

                const transformedData = transformApiData(apiData, dateFrom, dateTo);
                setRows(transformedData);

                setPagination(prev => ({
                    ...prev,
                    totalRecords: transformedData.length,
                    totalPages: Math.ceil(transformedData.length / prev.pageSize)
                }));
            } else {
                toast.info(response?.data?.Message || "Error fetching data.");
            }
        } catch (error) {
            toast.error("Failed to fetch data.");
        } finally {
            setIsLoading(false);
        }
    };

    const hanldeRefresh = async () => {
        try {
            const payload = {
                PropId: selectedProperty,
                Flag: ''
            };

            await Promise.all([
                axiosInstance.post('BedCalendar/InsertBedCat', payload),
                axiosInstance.post('BedCalendar/InsertBedCatCal', payload),
                axiosInstance.post('BedCalendar/UpdateBedCat', payload),
                axiosInstance.post('BedCalendar/UpdateBedCatCal_Data', payload),
            ]);
            toast.success("Latest data retrieved successfully.");
            await fetchData('');
        } catch (error) {
            toast.error("Error while refreshing the data.");
        }
    };

    const handleDropdownChange = async (event) => {
        const selectedId = event.target.value;
        dispatch(setLocalProperty({
            componentId: 'calendar',
            propertyId: selectedId
        }));
        setForm(prevForm => ({
            ...prevForm,
            PropId: selectedId
        }));
        fetchData();
    };

    const handleDateFromChange = (newDate) => {
        if (newDate && newDate.isValid()) {
            const startDate = newDate.startOf('month');
            setDateFrom(startDate);
            setDateTo(startDate.add(11, 'months'));
        }
    };

    const handleDateToChange = (newDate) => {
        if (newDate && newDate.isValid()) {

            setDateTo(newDate.startOf('month'));
        }
    };

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleHomeClick = () => {
        navigate('/dashboard');
    };

    const handleLocationclick = () => {
        navigate('/reports/dailysummaryreport');
    };

    const handleChangeRowsPerPage = (newPageSize) => {
        setRowsPerPage(newPageSize);
        setPage(0);
        setPagination(prev => ({
            ...prev,
            pageSize: newPageSize,
            totalPages: Math.ceil(prev.totalRecords / newPageSize)
        }));
    };

    const Filters = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <ToastContainer />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <FormControl sx={{ minWidth: 170 }}>
                    <InputLabel id="property-select-label">Property</InputLabel>
                    <Select
                        labelId="property-select-label"
                        value={selectedProperty || ''}
                        onChange={handleDropdownChange}
                        label="Property"
                        sx={{ height: "40px" }}
                    >
                        {properties.map((item) => (
                            <MenuItem key={item.Id} value={item.Id}>{item.Name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <DatePicker
                            label="From-Date"
                            value={dateFrom}
                            onChange={handleDateFromChange}
                            format="MM/YYYY"
                            views={['month', 'year']}
                            sx={{ backgroundColor: "white", width: 130 }}
                        />
                        <DatePicker
                            label="To-Date"
                            value={dateTo}
                            onChange={handleDateToChange}
                            format="MM/YYYY"
                            views={['month', 'year']}
                            minDate={dateFrom}
                            maxDate={dateFrom ? dateFrom.add(12, 'months') : null}
                            sx={{ backgroundColor: "white", width: 130 }}
                        />
                    </Box>
                </LocalizationProvider>

                <Tooltip title='Click to see data' arrow>
                    <Button
                        variant="contained"
                        onClick={() => {
                            const from = dayjs(dateFrom).startOf('month');
                            const to = dayjs(dateTo).startOf('month');
                            const diffMonths = to.diff(from, 'month', true); // true allows fractional months

                            if (diffMonths > 11) {
                                toast.warning("Date Range can't exceed 12 months.");
                                return;
                            }

                            if (diffMonths < 0) {
                                toast.warning("To-Date cannot be earlier than From-Date.");
                                return;
                            }

                            fetchData();
                        }}
                        sx={{
                            height: "40px",
                            whiteSpace: "nowrap",
                            backgroundColor: "#635bff",
                            borderRadius: '8px',
                            textTransform: 'none'
                        }}
                    >
                        Get Data
                    </Button>
                </Tooltip>
                <Tooltip title='Refresh latest data' arrow>
                    <Button
                        variant="contained"
                        onClick={hanldeRefresh}
                        sx={{
                            height: "40px",
                            whiteSpace: "nowrap",
                            backgroundColor: "#635bff",
                            borderRadius: '8px',
                            textTransform: 'none'
                        }}
                    >
                        Refresh
                    </Button>
                </Tooltip>
            </Box>
        </Box >
    );

    return (
        <Box sx={{ maxWidth: "100vw", overflowX: "hidden" }}>
            <TableComponent
                page={page}
                columns={tableColumns}
                rows={rows}
                title="Reports"
                titleMaster="Month Wise Bed Availablity"
                FiltersJsx={Filters}
                isLoading={isLoading}
                pagination={pagination}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPage={rowsPerPage}
                handleLocationclick={handleLocationclick}
                handleHomeClick={handleHomeClick}
            />
        </Box>
    );
}